import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from './Dialog'
import { Field } from './Field'
import { ChatWithParentButton } from './ChatWithParentButton'
import { ParentInviteSection } from './ParentInviteSection'
import { useEntityList, useEntitySave, useEntityDelete, makeEntity, now } from '../api/hooks/useEntity'
import type { SchoolClass, Student } from '../types/entities'
import { ENTITY } from '../types/entities'
import { studentFormSchema, validateForm } from '../utils/validation'

/** Форма создания/редактирования ученика. */
export function StudentForm({
  initial,
  onClose,
}: {
  initial: Student
  onClose: () => void
}) {
  const { t } = useTranslation()
  const classes = useEntityList<SchoolClass>(ENTITY.schoolClass)
  const save = useEntitySave<Student>(ENTITY.student)
  const del = useEntityDelete<Student>(ENTITY.student)

  const [lastName, setLastName] = useState(initial.lastName)
  const [firstName, setFirstName] = useState(initial.firstName)
  const [middleName, setMiddleName] = useState(initial.middleName ?? '')
  const [classId, setClassId] = useState(initial.classId)
  const [gender, setGender] = useState<'M' | 'F' | ''>(initial.gender ?? '')
  const [birthDate, setBirthDate] = useState(initial.birthDate ?? '')
  const [medicalGroup, setMedicalGroup] = useState<string>(initial.medicalGroup ?? '')
  const [specialNeeds, setSpecialNeeds] = useState(initial.specialNeeds ?? '')
  const [notes, setNotes] = useState(initial.notes ?? '')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const activeClasses = (classes.data ?? [])
    .filter((c) => c.archivedAt == null)
    .sort((a, b) => a.gradeLevel - b.gradeLevel || a.name.localeCompare(b.name))

  const isNew = !initial.id

  const handleSave = async () => {
    const formData = {
      lastName,
      firstName,
      middleName: middleName || null,
      classId,
      gender: gender || null,
      birthDate: birthDate || null,
      medicalGroup: (medicalGroup as 'main' | 'preparatory' | 'special' | 'exempt' | '') || null,
      specialNeeds: specialNeeds || null,
      notes: notes || null,
    }
    const { ok, errors: validationErrors } = validateForm(studentFormSchema, formData)
    if (!ok) {
      setErrors(validationErrors)
      return
    }
    setErrors({})
    const record = makeEntity<Student>({
      ...initial,
      lastName: lastName.trim(),
      firstName: firstName.trim(),
      middleName: middleName.trim() || null,
      classId,
      gender: gender || null,
      birthDate: birthDate || null,
      medicalGroup: (medicalGroup as Student['medicalGroup']) || null,
      specialNeeds: specialNeeds.trim() || null,
      notes: notes.trim() || null,
      updatedAt: now(),
    })
    await save.mutateAsync(record)
    onClose()
  }

  const handleDelete = async () => {
    if (!confirm(t('studentForm.confirmDelete', { name: `${lastName} ${firstName}` }))) return
    await del.mutateAsync(initial)
    onClose()
  }

  return (
    <Dialog title={isNew ? t('studentForm.titleNew') : t('studentForm.titleEdit')} onClose={onClose}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t('studentForm.lastName')}>
          <input
            className="input-field"
            placeholder={t('studentForm.lastNamePlaceholder')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoFocus
          />
          {errors.lastName && <div className="text-xs text-red-600 mt-1">{errors.lastName}</div>}
        </Field>
        <Field label={t('studentForm.firstName')}>
          <input
            className="input-field"
            placeholder={t('studentForm.firstNamePlaceholder')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          {errors.firstName && <div className="text-xs text-red-600 mt-1">{errors.firstName}</div>}
        </Field>
      </div>
      <Field label={t('studentForm.middleName')}>
        <input
          className="input-field"
          placeholder={t('studentForm.middleNamePlaceholder')}
          value={middleName}
          onChange={(e) => setMiddleName(e.target.value)}
        />
      </Field>
      <Field label={t('studentForm.classLabel')}>
        <select
          className="input-field"
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
        >
          <option value="">{t('studentForm.selectClass')}</option>
          {activeClasses.map((c) => (
            <option key={c.id} value={c.id}>
              {t('studentForm.classOption', { name: c.name, grade: c.gradeLevel })}
            </option>
          ))}
        </select>
        {errors.classId && <div className="text-xs text-red-600 mt-1">{errors.classId}</div>}
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t('studentForm.gender')}>
          <select
            className="input-field"
            value={gender}
            onChange={(e) => setGender(e.target.value as 'M' | 'F' | '')}
          >
            <option value="">—</option>
            <option value="M">{t('studentForm.male')}</option>
            <option value="F">{t('studentForm.female')}</option>
          </select>
        </Field>
        <Field label={t('studentForm.birthDate')}>
          <input
            type="date"
            className="input-field"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
          />
        </Field>
      </div>
      <Field label={t('studentForm.medicalGroup')}>
        <select
          className="input-field"
          value={medicalGroup}
          onChange={(e) => setMedicalGroup(e.target.value)}
        >
          <option value="">—</option>
          <option value="main">{t('studentForm.mgMain')}</option>
          <option value="preparatory">{t('studentForm.mgPreparatory')}</option>
          <option value="special">{t('studentForm.mgSpecial')}</option>
          <option value="exempt">{t('studentForm.mgExempt')}</option>
        </select>
      </Field>
      <Field label={t('studentForm.specialNeeds')}>
        <input
          className="input-field"
          placeholder={t('studentForm.specialNeedsPlaceholder')}
          value={specialNeeds}
          onChange={(e) => setSpecialNeeds(e.target.value)}
        />
      </Field>
      <Field label={t('studentForm.notes')}>
        <textarea
          className="input-field min-h-[60px]"
          placeholder={t('studentForm.notesPlaceholder')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>

      {!isNew && (
        <>
          <ParentInviteSection studentId={initial.id} studentName={`${initial.lastName} ${initial.firstName}`} />
          <ChatWithParentButton studentId={initial.id} />
        </>
      )}

      <div className="flex gap-2 justify-between flex-wrap mt-4">
        <div>
          {!isNew && (
            <button
              onClick={handleDelete}
              disabled={del.isPending}
              className="text-sm text-red-600 hover:text-red-700 px-3 py-2"
            >
              {t('common.delete')}
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-outline text-sm">
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={save.isPending}
            className="btn-primary text-sm"
          >
            {save.isPending ? t('studentForm.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </Dialog>
  )
}

export function blankStudent(classId: string = ''): Student {
  return {
    id: '',
    classId,
    lastName: '',
    firstName: '',
    middleName: null,
    gender: null,
    birthDate: null,
    parentContacts: [],
    medicalGroup: null,
    specialNeeds: null,
    notes: null,
    tags: [],
    createdAt: 0,
    updatedAt: 0,
  }
}
